/**
 * System Rule Detail View
 * Readonly view for system rules with ability to enable/disable
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
} from '@mui/material';
import Lock from '@mui/icons-material/Lock';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Block from '@mui/icons-material/Block';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PlayArrow from '@mui/icons-material/PlayArrow';
import { ViewToolbar } from '@/components/ViewToolbar';
import { businessRulesService, type UnifiedRule } from '@/services/core/BusinessRulesService';
import { RuleSeverityChip } from '@/core/rules/components/RuleSeverityChip';
import { buildOverridePreFill } from '@/core/rules/overridePreFill';
import { describeRunsOn } from '@/core/rules/runsOn';

/**
 * System Rule Detail View
 * Shows system rule in readonly mode with option to disable/enable or create override
 */
export const SystemRuleDetailView = () => {
  const { t } = useTranslation();
  const { id: _id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get system rule data from router state
  const initialRule = (location.state as { systemRule?: UnifiedRule })?.systemRule;

  const [systemRule, setSystemRule] = useState<UnifiedRule | undefined>(initialRule);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleBack = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo);
      return;
    }
    navigate('/core/businessrules');
  };

  const handleCreateOverride = () => {
    if (!systemRule) return;

    // The same pre-fill the Business Rules grid builds — knobs, severity, scope and all.
    navigate('/core/businessrules/new', {
      state: { preFill: buildOverridePreFill(systemRule) }
    });
  };

  const handleDisableRule = async () => {
    if (!systemRule) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await businessRulesService.disableSystemRule(systemRule.id);
      // Update local state to reflect the change
      setSystemRule({
        ...systemRule,
        enabled: false,
        isDisabledForTenant: true,
        status: 'Disabled',
      });
      setSuccessMessage(t('systemRule.disabled'));
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('systemRule.disableFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnableRule = async () => {
    if (!systemRule) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await businessRulesService.enableSystemRule(systemRule.id);
      // Update local state to reflect the change
      setSystemRule({
        ...systemRule,
        enabled: true,
        isDisabledForTenant: false,
        status: 'Active',
      });
      setSuccessMessage(t('systemRule.enabled'));
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('systemRule.enableFailed'));
    } finally {
      setLoading(false);
    }
  };

  // No data available - redirect back to list
  if (!systemRule) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('systemRule.notFound')}
        </Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          {t('systemRule.backToList')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <ViewToolbar onBack={handleBack}>
        {systemRule.canBeOverridden && (
          <>
            {systemRule.isDisabledForTenant ? (
              <Button
                variant="outlined"
                color="success"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                onClick={handleEnableRule}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 500, px: 2 }}
              >
                {t('systemRule.enableRule')}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="error"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Block />}
                onClick={handleDisableRule}
                disabled={loading}
                sx={{ textTransform: 'none', fontWeight: 500, px: 2 }}
              >
                {t('systemRule.disableRule')}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={handleCreateOverride}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 500, px: 2 }}
            >
              {t('businessRules.createOverride')}
            </Button>
          </>
        )}
      </ViewToolbar>

      {/* Main Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2, bgcolor: 'background.default' }}>
        {/* Page Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Lock color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {t('businessRules.systemRuleTitle', { name: systemRule.name })}
          </Typography>
          <Chip
            label={t('systemRule.readOnly')}
            size="small"
            color="primary"
            variant="outlined"
          />
          {/* C8: an advisory rule announces itself in the title, not only under Error Handling */}
          <RuleSeverityChip severity={systemRule.errorType} />
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Disabled Banner */}
        {systemRule.isDisabledForTenant && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('systemRule.disabledBanner')}
          </Alert>
        )}

        {/* Info Banner */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('systemRule.infoBanner')}
          {systemRule.canBeOverridden
            ? t('systemRule.infoOverridable')
            : t('systemRule.infoNotOverridable')}
        </Alert>

        {/* Rule Identity */}
        <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
            {t('systemRule.identity')}
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 180, border: 0 }}>{t('DisplayName:BusinessRule.ruleNumber')}</TableCell>
                <TableCell sx={{ border: 0 }}>{systemRule.ruleNumber}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: 0 }}>{t('DisplayName:BusinessRule.ruleKey')}</TableCell>
                <TableCell sx={{ border: 0 }}><code>{systemRule.ruleKey}</code></TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: 0 }}>{t('DisplayName:BusinessRule.name')}</TableCell>
                <TableCell sx={{ border: 0 }}>{systemRule.name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: 0 }}>{t('DisplayName:BusinessRule.description')}</TableCell>
                <TableCell sx={{ border: 0 }}>{systemRule.description || '—'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        {/* Target & Configuration */}
        <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
            {t('systemRule.targetConfig')}
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 180, border: 0 }}>{t('DisplayName:BusinessRule.module')}</TableCell>
                <TableCell sx={{ border: 0 }}>{systemRule.module}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: 0 }}>{t('DisplayName:BusinessRule.entity')}</TableCell>
                <TableCell sx={{ border: 0 }}>{systemRule.entity}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: 0 }}>{t('DisplayName:BusinessRule.priority')}</TableCell>
                <TableCell sx={{ border: 0 }}>{systemRule.priority}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: 0 }}>{t('DisplayName:BusinessRule.enabled')}</TableCell>
                <TableCell sx={{ border: 0 }}>
                  <Chip
                    icon={systemRule.enabled && !systemRule.isDisabledForTenant ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
                    label={systemRule.isDisabledForTenant ? t('systemRule.disabledForTenant') : systemRule.enabled ? t('common.yes') : t('common.no')}
                    size="small"
                    color={systemRule.enabled && !systemRule.isDisabledForTenant ? 'success' : 'default'}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: 0 }}>{t('businessRules.canBeOverridden')}</TableCell>
                <TableCell sx={{ border: 0 }}>
                  <Chip
                    label={systemRule.canBeOverridden ? t('common.yes') : t('common.no')}
                    size="small"
                    color={systemRule.canBeOverridden ? 'success' : 'error'}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        {/* Trigger — what the rule runs on (C18: the enumerated scope, never a bare kind) */}
        {systemRule.trigger && (
          <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
              {t('systemRule.trigger')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('businessRules.runsOn')}:</strong> {describeRunsOn(systemRule, t)}
            </Typography>
            <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
              {systemRule.trigger}
            </Box>
          </Paper>
        )}

        {/* Condition */}
        {(systemRule.when || systemRule.expression) && (
          <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
              {t('systemRule.condition')}
            </Typography>
            {systemRule.when && (
              <Box sx={{ mb: systemRule.expression ? 2 : 0 }}>
                <Typography variant="caption" color="text.secondary">{t('systemRule.whenAst')}</Typography>
                <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {typeof systemRule.when === 'string'
                      ? (() => {
                          try { return JSON.stringify(JSON.parse(systemRule.when), null, 2); }
                          catch { return systemRule.when; }
                        })()
                      : JSON.stringify(systemRule.when, null, 2)}
                  </pre>
                </Box>
              </Box>
            )}
            {systemRule.expression && (
              <Box>
                <Typography variant="caption" color="text.secondary">{t('systemRule.expressionLambda')}</Typography>
                <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.875rem', overflow: 'auto' }}>
                  {systemRule.expression}
                </Box>
              </Box>
            )}
          </Paper>
        )}

        {/* Actions */}
        {systemRule.actions && (
          <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
              {t('systemRule.actions')}
            </Typography>
            <Box sx={{ p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'auto', maxHeight: 200 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {typeof systemRule.actions === 'string'
                  ? (() => {
                      try { return JSON.stringify(JSON.parse(systemRule.actions), null, 2); }
                      catch { return systemRule.actions; }
                    })()
                  : JSON.stringify(systemRule.actions, null, 2)}
              </pre>
            </Box>
          </Paper>
        )}

        {/* Error Handling */}
        {(systemRule.errorMessage || systemRule.errorType) && (
          <Paper sx={{ p: 2, mb: 2 }} elevation={1}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em' }}>
              {t('systemRule.errorHandling')}
            </Typography>
            <Table size="small">
              <TableBody>
                {systemRule.errorType && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: 180, border: 0 }}>{t('systemRule.errorType')}</TableCell>
                    <TableCell sx={{ border: 0 }}><RuleSeverityChip severity={systemRule.errorType} /></TableCell>
                  </TableRow>
                )}
                {systemRule.errorMessage && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, border: 0 }}>{t('DisplayName:BusinessRule.errorMessage')}</TableCell>
                    <TableCell sx={{ border: 0 }}>{systemRule.errorMessage}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>
    </Box>
  );
};
